/*
 * lotto/sync.js — the host's draw on the players' phones, live.
 *
 * Topology: one host, many phones (room-connect host-N). The host is the only
 * source of truth and SENDS the whole draw on every change — a phone that
 * joins late, reconnects after sleep or missed a message is correct after the
 * next one, and an Undo on the host is an Undo on every phone. Phones send
 * nothing back: a player's marks never travel, so nobody can «mark» on
 * someone else's behalf.
 *
 * The broker and ICE servers are the site's own (webrtc-config.js →
 * __WebRTC.createPeer, regional by the page locale): a room lives on one
 * broker, and the player's link carries the same locale as the host's page.
 */

export const PREFIX = 'lotto-';
const MSG = 'lt';
const HELLO = 'lt-hi';

let rtcPromise = null;
/* PeerJS, the regional config and the room layer — loaded only when someone
 * actually opens or joins a room. `load(list, label, probe)` is the page's
 * script loader (asset chain first, a plain <script> otherwise). */
export function ensureRtc(assets, load) {
    if (window.__RoomConnect && window.Peer && window.__WebRTC) return Promise.resolve();
    if (!rtcPromise) {
        rtcPromise = load(assets.peerjs, 'peerjs', () => window.Peer)
            .then(() => load(assets.webrtc, 'webrtc-config', () => window.__WebRTC))
            .then(() => load(assets.room, 'room-connect', () => window.__RoomConnect))
            .catch((e) => { rtcPromise = null; throw e; });
    }
    return rtcPromise;
}

export function stateMessage(game, set, drawn) {
    return { t: MSG, v: 1, game, set, drawn: drawn.slice() };
}

/* A message is trusted only in its exact shape: numbers in range, no repeats. */
export function readMessage(msg, games) {
    if (!msg || msg.t !== MSG || !games[msg.game] || typeof msg.set !== 'string' || !Array.isArray(msg.drawn)) return null;
    const max = games[msg.game].max, seen = new Set();
    for (const x of msg.drawn) {
        if (!Number.isInteger(x) || x < 1 || x > max || seen.has(x)) return null;
        seen.add(x);
    }
    return { game: msg.game, set: msg.set, drawn: msg.drawn.slice() };
}

/*
 * openHost({ code, current(), onOpen(code), onCount(n), onError(type) })
 * → { code, send(), close() }
 */
export function openHost(o) {
    const RC = window.__RoomConnect;
    let code = o.code || RC.generateCode(6);
    let room = null;
    const count = () => { try { o.onCount && o.onCount(room ? room.connections.size : 0); } catch (_) {} };
    room = RC.hostRoom({
        prefix: PREFIX,
        code,
        maxGuests: 120,
        enableVisibilityRecovery: true,
        tool: 'lotto',
        regenCode: () => { code = RC.generateCode(6); return code; },
        onPeerOpen: () => { try { o.onOpen && o.onOpen(code); } catch (_) {} },
        onGuestConnect: (conn) => {
            try { conn.send(o.current()); } catch (_) {}
            // A phone asks again once its listener is surely in place: the
            // first frame can race the guest's own `open` (room-connect.js,
            // the /checkers measurement), and a lost first frame would leave
            // the phone blank until the next barrel.
            conn.on('data', (m) => { if (m && m.t === HELLO) { try { conn.send(o.current()); } catch (_) {} } });
            conn.on('close', () => setTimeout(count, 0));
            count();
        },
        onError: (err) => { try { o.onError && o.onError(String((err && err.type) || 'error')); } catch (_) {} },
    });
    return {
        get code() { return code; },
        send() { if (room) room.broadcast(o.current()); },
        count,
        close() { if (room) { room.destroy(); room = null; } },
    };
}

/*
 * joinHost({ code, onState(state), onStatus(kind), games })
 * kind: 'connecting' | 'connected' | 'not_found' | 'closed' | 'error'
 */
export function joinHost(o) {
    const RC = window.__RoomConnect;
    const v = RC.validateCodeDetailed(String(o.code || ''));
    if (v.error) { o.onStatus('bad_code'); return { close() {} }; }
    o.onStatus('connecting');
    let conn = null, room = null;
    room = RC.joinRoom({
        prefix: PREFIX,
        code: v.code,
        timeoutMs: 15000,
        enableVisibilityRecovery: true,
        tool: 'lotto',
        onConnected: (c) => {
            conn = c;
            o.onStatus('connected', v.code);
            c.on('data', (msg) => { const st = readMessage(msg, o.games); if (st) o.onState(st); });
            c.on('close', () => o.onStatus('closed'));
            try { c.send({ t: HELLO }); } catch (_) {}
        },
        onRoomNotFound: () => o.onStatus('not_found'),
        onRoomFull: () => o.onStatus('error'),
        onClose: () => o.onStatus('closed'),
        onError: (err) => o.onStatus((err && err.type) === 'peer-unavailable' ? 'not_found' : 'error'),
    });
    return {
        code: v.code,
        close() { try { conn && conn.close(); } catch (_) {} try { room && room.destroy && room.destroy(); } catch (_) {} },
    };
}
